import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0TagRaw, F0Icon } from '@factorialco/f0-react'
import { Sparkles, Check } from '@factorialco/f0-react/icons/app'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import PriceInfo from 'modules/upsell/components/SelfUpsell/UpgradePlanModal/PriceInfo'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { BUSINESS_VARIANT, ENTERPRISE_VARIANT } from 'modules/upsell/lib/selfUpsell/plans/utils'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'
import type { VariantsEnum } from 'modules/upsell/lib/selfUpsell/types'

type VariantSelectorProps = {
  addon: Plan
  crossSellingAddon?: Plan | undefined
  showPrice: boolean
  setSelectedAddonId: (id: string) => void
  installedAddonInternalId?: string | null
}

const VariantSelector = ({
  addon,
  crossSellingAddon,
  showPrice,
  setSelectedAddonId,
  installedAddonInternalId,
}: VariantSelectorProps) => {
  const {
    configPlan,
    setConfigPlan,
    period,
    togglePlanSelect,
    currency,
    discount,
    setSeats,
    seats,
    currentSeats,
    isExpenses,
  } = useSelfUpsellContext()

  useEffect(() => {
    const hasEnterprise = addon.variantTypes.includes(ENTERPRISE_VARIANT)
    const hasBusiness = addon.variantTypes.includes(BUSINESS_VARIANT)
    if (!hasEnterprise && !hasBusiness) return

    if (configPlan.variant && addon.variantTypes.includes(configPlan.variant)) return
    if (!addon.isSelected) togglePlanSelect(addon)

    const nextVariant = hasEnterprise ? ENTERPRISE_VARIANT : BUSINESS_VARIANT
    addon.setActiveVariant(nextVariant)
    if (isExpenses()) {
      const enterpriseTieredPrice = addon
        .getSpecificVariant(nextVariant)
        ?.pricing.getTieredPrice(period, seats)
      setSeats(enterpriseTieredPrice?.tiers?.[0]?.endingUnit || 1)
    }

    const addons = [...addon.pricing.getAllPlanIds(period)]
    const crossSellingPlanId = crossSellingAddon?.pricing.getPlanId(period)
    if (crossSellingPlanId) {
      addons.push(crossSellingPlanId)
    }

    setConfigPlan({
      addon: addons,
      variant: nextVariant,
    })
  }, [])

  return (
    <Box gap="s6">
      <Text.Normal size="100" color="grey700" weight="medium">
        {i18n.t('self_upsell.first_step.select_plan_type')}
      </Text.Normal>
      <Box gap="s8">
        <Box
          border={{
            all: {
              width: 's1',
              style: 'solid',
              color: configPlan.variant === ENTERPRISE_VARIANT ? 'viridian1000' : 'grey400',
            },
          }}
          borderRadius={{ all: 'abs016' }}
          background={configPlan.variant === ENTERPRISE_VARIANT ? 'viridian100' : 'grey000White'}
          padding="s16"
          gap="s8"
          justifyContent="spaceBetween"
          style={{
            height: '88px',
          }}
        >
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
            <Text.Normal size="200" weight="semibold" color="grey1000">
              {ENTERPRISE_VARIANT}
            </Text.Normal>
            <F0TagRaw text="Best value" icon={Sparkles} />
          </Box>
          {showPrice && (
            <PriceInfo
              variant={addon.getSpecificVariant(ENTERPRISE_VARIANT)}
              period={period}
              currency={currency}
              seats={seats}
              currentSeats={currentSeats}
              discount={discount}
              disabled={false}
            />
          )}
        </Box>

        <Box
          border={{
            all: {
              width: 's1',
              style: 'solid',
              color: 'grey400',
            },
          }}
          borderRadius={{ all: 'abs016' }}
          background={'grey000White'}
          padding="s16"
          gap="s8"
          justifyContent="spaceBetween"
          style={{
            height: '88px',
          }}
        >
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
            <Text.Normal size="200" weight="semibold" color="grey600">
              {BUSINESS_VARIANT}
            </Text.Normal>
            <Box className="text-color-grey flex-row items-center pr-2">
              <F0Icon icon={Check} color="secondary" width="16px" height="16px" />
              <Text.Normal size="200" color="grey600">
                {i18n.t('marketplace.revamp.in_your_plan')}
              </Text.Normal>
            </Box>
          </Box>
          {showPrice && (
            <PriceInfo
              variant={addon.getSpecificVariant(BUSINESS_VARIANT)}
              period={period}
              currency={currency}
              seats={seats}
              currentSeats={currentSeats}
              discount={discount}
              disabled
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default VariantSelector
